import { ProcessPhotoJob, ProcessPhotoResult } from '../types/domain';

export type { ProcessPhotoJob, ProcessPhotoResult };

export type WorkerInMessage =
  | { type: 'PROCESS_PHOTO'; payload: ProcessPhotoJob }
  | { type: 'TERMINATE' };

export type WorkerOutMessage =
  | { type: 'SUCCESS'; payload: ProcessPhotoResult }
  | { type: 'ERROR'; id: string; error: string };

export interface IngestionProgressStats {
  completed: number;
  total: number;
  successful: number;
  failed: number;
  bytesProcessed: number;
  photosPerSecond: number;
  estimatedRemainingSeconds: number;
}

export interface WorkerPoolOptions {
  maxWorkers?: number;
  workerScriptUrl?: URL | string;
  workerFactory?: () => Worker;
}

export interface BatchStreamCallbacks {
  onResult: (result: ProcessPhotoResult, index: number, total: number) => Promise<void> | void;
  onError?: (error: Error, job: ProcessPhotoJob, index: number, total: number) => void;
  onProgress?: (stats: IngestionProgressStats) => void;
  signal?: AbortSignal;
}

interface WorkerSlot {
  id: number;
  worker: Worker;
  busy: boolean;
  activeJobId: string | null;
}

interface QueuedTask {
  job: ProcessPhotoJob;
  resolve: (value: ProcessPhotoResult) => void;
  reject: (reason?: any) => void;
  signal?: AbortSignal;
}

/**
 * Resolves the optimal worker concurrency based on hardware cores.
 * Clamps between 1 and 4, reserving at least 1 core for the main thread.
 */
export function resolveOptimalWorkerCount(): number {
  if (typeof navigator === 'undefined' || !navigator.hardwareConcurrency) {
    return 3;
  }
  const cores = navigator.hardwareConcurrency;
  return Math.min(4, Math.max(1, cores - 1));
}

export class WorkerPool {
  private workers: WorkerSlot[] = [];
  private queue: QueuedTask[] = [];
  private inFlightTasks = new Map<string, QueuedTask>();
  private maxWorkers: number;
  private isTerminated = false;
  private workerScriptUrl?: URL | string;
  private workerFactory?: () => Worker;

  constructor(options?: WorkerPoolOptions) {
    const defaultWorkers = resolveOptimalWorkerCount();
    this.maxWorkers = options?.maxWorkers
      ? Math.min(4, Math.max(1, options.maxWorkers))
      : defaultWorkers;
    this.workerScriptUrl = options?.workerScriptUrl;
    this.workerFactory = options?.workerFactory;
  }

  get concurrency(): number {
    return this.maxWorkers;
  }

  get queueLength(): number {
    return this.queue.length;
  }

  get activeWorkers(): number {
    return this.workers.filter((w) => w.busy).length;
  }

  private createWorkerInstance(): Worker {
    if (this.workerFactory) {
      return this.workerFactory();
    }
    if (this.workerScriptUrl) {
      return new Worker(this.workerScriptUrl, { type: 'module' });
    }
    return new Worker(new URL('./imageWorker.ts', import.meta.url), { type: 'module' });
  }

  private initWorkerSlot(id: number): WorkerSlot {
    const worker = this.createWorkerInstance();
    const slot: WorkerSlot = {
      id,
      worker,
      busy: false,
      activeJobId: null,
    };

    worker.onmessage = (e: MessageEvent<WorkerOutMessage>) => {
      this.handleWorkerMessage(slot, e.data);
    };

    worker.onerror = (err: ErrorEvent | any) => {
      this.handleWorkerError(slot, err);
    };

    return slot;
  }

  private handleWorkerMessage(slot: WorkerSlot, msg: WorkerOutMessage) {
    slot.busy = false;
    slot.activeJobId = null;

    if (msg.type === 'SUCCESS') {
      const activeTask = this.inFlightTasks.get(msg.payload.id);
      if (activeTask) {
        this.inFlightTasks.delete(msg.payload.id);
        activeTask.resolve(msg.payload);
      }
    } else if (msg.type === 'ERROR') {
      const activeTask = this.inFlightTasks.get(msg.id);
      if (activeTask) {
        this.inFlightTasks.delete(msg.id);
        activeTask.reject(new Error(msg.error));
      }
    }

    this.dispatchNext();
  }

  private handleWorkerError(slot: WorkerSlot, err: ErrorEvent | any) {
    const failedJobId = slot.activeJobId;
    slot.busy = false;
    slot.activeJobId = null;

    if (failedJobId) {
      const activeTask = this.inFlightTasks.get(failedJobId);
      if (activeTask) {
        this.inFlightTasks.delete(failedJobId);
        const errMsg = err?.message || 'unknown worker error';
        activeTask.reject(new Error(`Worker crash: ${errMsg}`));
      }
    }

    // Terminate crashed worker and recreate fresh instance in slot
    try {
      slot.worker.terminate();
    } catch {}

    if (!this.isTerminated) {
      try {
        const replacement = this.initWorkerSlot(slot.id);
        const idx = this.workers.findIndex((w) => w.id === slot.id);
        if (idx !== -1) {
          this.workers[idx] = replacement;
        }
      } catch {}
    }

    this.dispatchNext();
  }

  private dispatchNext() {
    if (this.isTerminated || this.queue.length === 0) return;

    // Check for available slot or spawn lazily
    let slot = this.workers.find((w) => !w.busy);
    if (!slot && this.workers.length < this.maxWorkers) {
      slot = this.initWorkerSlot(this.workers.length);
      this.workers.push(slot);
    }

    if (!slot || slot.busy) return;

    const task = this.queue.shift();
    if (!task) return;

    if (task.signal?.aborted) {
      task.reject(new DOMException('Aborted', 'AbortError'));
      this.dispatchNext();
      return;
    }

    slot.busy = true;
    slot.activeJobId = task.job.id;
    this.inFlightTasks.set(task.job.id, task);

    slot.worker.postMessage({
      type: 'PROCESS_PHOTO',
      payload: task.job,
    } as WorkerInMessage);
  }

  public enqueue(job: ProcessPhotoJob, signal?: AbortSignal): Promise<ProcessPhotoResult> {
    if (this.isTerminated) {
      return Promise.reject(new Error('WorkerPool is terminated'));
    }

    return new Promise<ProcessPhotoResult>((resolve, reject) => {
      if (signal?.aborted) {
        return reject(new DOMException('Aborted', 'AbortError'));
      }

      const task: QueuedTask = { job, resolve, reject, signal };

      if (signal) {
        signal.addEventListener(
          'abort',
          () => {
            const idx = this.queue.indexOf(task);
            if (idx !== -1) {
              this.queue.splice(idx, 1);
              reject(new DOMException('Aborted', 'AbortError'));
            }
          },
          { once: true }
        );
      }

      this.queue.push(task);
      this.dispatchNext();
    });
  }

  /**
   * Alias for enqueue, providing processPhoto method name consistency.
   */
  public processPhoto(job: ProcessPhotoJob, signal?: AbortSignal): Promise<ProcessPhotoResult> {
    return this.enqueue(job, signal);
  }

  public async processBatchStream(
    jobs: ProcessPhotoJob[],
    callbacks: BatchStreamCallbacks
  ): Promise<{ completed: number; successful: number; failed: number; durationMs: number }> {
    const startTime = performance.now();
    const total = jobs.length;
    let completed = 0;
    let successful = 0;
    let failed = 0;
    let bytesProcessed = 0;

    return new Promise((resolve) => {
      if (total === 0) {
        return resolve({ completed: 0, successful: 0, failed: 0, durationMs: 0 });
      }

      jobs.forEach((job, index) => {
        bytesProcessed += (job.file as any).size || 0;

        this.enqueue(job, callbacks.signal)
          .then(async (result) => {
            completed++;
            successful++;

            try {
              await callbacks.onResult(result, index, total);
            } catch (cbErr: any) {
              callbacks.onError?.(cbErr, job, index, total);
            }

            const elapsedSec = (performance.now() - startTime) / 1000;
            const photosPerSec = elapsedSec > 0 ? completed / elapsedSec : 0;
            const remaining = photosPerSec > 0 ? (total - completed) / photosPerSec : 0;

            callbacks.onProgress?.({
              completed,
              total,
              successful,
              failed,
              bytesProcessed,
              photosPerSecond: Math.round(photosPerSec * 10) / 10,
              estimatedRemainingSeconds: Math.ceil(remaining),
            });

            if (completed === total) {
              resolve({
                completed,
                successful,
                failed,
                durationMs: Math.round(performance.now() - startTime),
              });
            }
          })
          .catch((err) => {
            completed++;
            failed++;

            callbacks.onError?.(err, job, index, total);

            const elapsedSec = (performance.now() - startTime) / 1000;
            const photosPerSec = elapsedSec > 0 ? completed / elapsedSec : 0;
            const remaining = photosPerSec > 0 ? (total - completed) / photosPerSec : 0;

            callbacks.onProgress?.({
              completed,
              total,
              successful,
              failed,
              bytesProcessed,
              photosPerSecond: Math.round(photosPerSec * 10) / 10,
              estimatedRemainingSeconds: Math.ceil(remaining),
            });

            if (completed === total) {
              resolve({
                completed,
                successful,
                failed,
                durationMs: Math.round(performance.now() - startTime),
              });
            }
          });
      });
    });
  }

  public terminate(): void {
    this.isTerminated = true;
    for (const task of this.queue) {
      task.reject(new DOMException('WorkerPool terminated', 'AbortError'));
    }
    this.queue = [];
    for (const task of this.inFlightTasks.values()) {
      task.reject(new DOMException('WorkerPool terminated', 'AbortError'));
    }
    this.inFlightTasks.clear();

    for (const slot of this.workers) {
      try {
        slot.worker.terminate();
      } catch {}
    }
    this.workers = [];
  }
}
